// Travel Insurance Files Storage
// Temporary in-memory storage for document files during the policy flow
// Files are stored here because Redux cannot serialize File/Blob objects

// Storage map for travel insurance documents
const travelFiles = new Map();

// File keys for travel insurance documents
export const TRAVEL_FILE_KEYS = {
  FLIGHT_TICKET: 'travel_flight_ticket',
  PASSPORT: 'travel_passport',
  CPR_FRONT: 'travel_cpr_front',
  CPR_BACK: 'travel_cpr_back',
};

/**
 * Store a file for travel insurance policy
 * @param {string} key - File key (use TRAVEL_FILE_KEYS)
 * @param {File} file - The file to store
 */
export const storeTravelFile = (key, file) => {
  if (file instanceof File || file instanceof Blob) {
    travelFiles.set(key, file);
    console.log('[Travel Storage] Stored file:', key, file.name || 'blob');
  }
};

/**
 * Get a stored file
 * @param {string} key - File key
 * @returns {File|null} The stored file or null
 */
export const getTravelFile = (key) => {
  return travelFiles.get(key) || null;
};

/**
 * Get all stored travel files
 * @returns {Object} Object with all stored files
 */
export const getAllTravelFiles = () => {
  return {
    flightTicket: travelFiles.get(TRAVEL_FILE_KEYS.FLIGHT_TICKET) || null,
    passport: travelFiles.get(TRAVEL_FILE_KEYS.PASSPORT) || null,
    cprFront: travelFiles.get(TRAVEL_FILE_KEYS.CPR_FRONT) || null,
    cprBack: travelFiles.get(TRAVEL_FILE_KEYS.CPR_BACK) || null,
  };
};

/**
 * Get all files as an array for Vista submission
 * @returns {File[]} Array of files in order [flightTicket, passport, cprFront, cprBack]
 */
export const getTravelFilesArray = () => {
  const files = [];
  const flightTicket = travelFiles.get(TRAVEL_FILE_KEYS.FLIGHT_TICKET);
  const passport = travelFiles.get(TRAVEL_FILE_KEYS.PASSPORT);
  const cprFront = travelFiles.get(TRAVEL_FILE_KEYS.CPR_FRONT);
  const cprBack = travelFiles.get(TRAVEL_FILE_KEYS.CPR_BACK);

  if (flightTicket) files.push(flightTicket);
  if (passport) files.push(passport);
  if (cprFront) files.push(cprFront);
  if (cprBack) files.push(cprBack);

  return files;
};

/**
 * Get document types array matching the files order
 * @returns {string[]} Array of document type labels
 */
export const getTravelDocumentTypes = () => {
  const types = [];
  if (travelFiles.has(TRAVEL_FILE_KEYS.FLIGHT_TICKET)) types.push('Flight Tickets');
  if (travelFiles.has(TRAVEL_FILE_KEYS.PASSPORT)) types.push('Passport');
  if (travelFiles.has(TRAVEL_FILE_KEYS.CPR_FRONT)) types.push('CPR (Front)');
  if (travelFiles.has(TRAVEL_FILE_KEYS.CPR_BACK)) types.push('CPR (Back)');
  return types;
};

/**
 * Remove a specific file
 * @param {string} key - File key to remove
 */
export const removeTravelFile = (key) => {
  travelFiles.delete(key);
  console.log('[Travel Storage] Removed file:', key);
};

/**
 * Clear all stored travel files
 * Call this after successful policy creation or when user exits flow
 */
export const clearTravelFiles = () => {
  travelFiles.clear();
  // Also clear persisted basic-info entered on the start page so the form
  // does not auto-restore on the next visit to the travel flow.
  try {
    localStorage.removeItem('travel_basic_info');
  } catch (error) {
    console.error('[Travel Storage] Error clearing travel_basic_info:', error);
  }
  console.log('[Travel Storage] Cleared all files');
};

/**
 * Get count of stored files
 * @returns {number} Number of stored files
 */
export const getTravelFilesCount = () => {
  return travelFiles.size;
};

/**
 * Check if minimum required files are stored (flight ticket + CPR)
 * Passport is optional
 * @returns {boolean} True if minimum required files are present
 */
export const hasRequiredTravelFiles = () => {
  return (
    travelFiles.has(TRAVEL_FILE_KEYS.FLIGHT_TICKET) &&
    travelFiles.has(TRAVEL_FILE_KEYS.CPR_FRONT) &&
    travelFiles.has(TRAVEL_FILE_KEYS.CPR_BACK)
  );
};

/**
 * Check if all files including passport are stored
 * @returns {boolean} True if all files are present
 */
export const hasAllTravelFiles = () => {
  return (
    travelFiles.has(TRAVEL_FILE_KEYS.FLIGHT_TICKET) &&
    travelFiles.has(TRAVEL_FILE_KEYS.PASSPORT) &&
    travelFiles.has(TRAVEL_FILE_KEYS.CPR_FRONT) &&
    travelFiles.has(TRAVEL_FILE_KEYS.CPR_BACK)
  );
};

export default {
  storeTravelFile,
  getTravelFile,
  getAllTravelFiles,
  getTravelFilesArray,
  getTravelDocumentTypes,
  removeTravelFile,
  clearTravelFiles,
  getTravelFilesCount,
  hasRequiredTravelFiles,
  hasAllTravelFiles,
  TRAVEL_FILE_KEYS,
};
